import * as React from "react";
import { useRecorder } from "hooks";
import {
  StartConversationLogRequest,
  useStartConversationLog,
} from "../api/mutations/useConversationLogMutations";

interface UseConversationRecordingProps {
  encounterId: string;
  chartCode: string;
}

interface UseConversationRecordingResult {
  isRecording: boolean;
  isUploading: boolean;
  startListening: () => Promise<void>;
  stopListening: () => Promise<{ audioBlob: Blob; audioDuration: number }>;
  cancelListening: () => void;
  audioBlob: Blob | null;
  audioDuration: number;
  conversationLogId: string | null;
}

interface PendingUpload {
  resolve: (value: { audioBlob: Blob; audioDuration: number }) => void;
  reject: (reason: Error) => void;
}

function useConversationRecording(
  props: UseConversationRecordingProps
): UseConversationRecordingResult {
  const { encounterId, chartCode } = props;
  const recorder = useRecorder({ maxMinutesRecord: 30 });
  const startMutation = useStartConversationLog(encounterId);

  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [conversationLogId, setConversationLogId] = React.useState<
    string | null
  >(null);
  const [audioDuration, setAudioDuration] = React.useState<number>(0);

  const audioUploadUrlRef = React.useRef<string | null>(null);
  const pendingUploadRef = React.useRef<PendingUpload | null>(null);

  const startListening = React.useCallback(async () => {
    if (recorder.state.initRecording) {
      return;
    }

    const startRequest: StartConversationLogRequest = { chartCode };
    const startResponse = await startMutation.mutateAsync(startRequest);

    setConversationLogId(startResponse.data.conversationLogId);
    audioUploadUrlRef.current = startResponse.data.audioUploadUrl;

    await recorder.startRecording();
  }, [chartCode, recorder, startMutation]);

  const stopListening = React.useCallback(async () => {
    if (!recorder.state.initRecording) {
      throw new Error("Cannot stop recording because recording is not active");
    }

    if (!audioUploadUrlRef.current) {
      throw new Error("Audio upload URL is not available");
    }

    const duration = recorder.state.minutes * 60 + recorder.state.seconds;
    setAudioDuration(duration);

    recorder.saveRecording();

    return new Promise<{ audioBlob: Blob; audioDuration: number }>(
      (resolve, reject) => {
        pendingUploadRef.current = { resolve, reject };
      }
    );
  }, [recorder]);

  const { cancelRecording } = recorder;

  const cancelListening = React.useCallback(() => {
    const pendingUpload = pendingUploadRef.current;
    pendingUploadRef.current = null;
    cancelRecording();
    pendingUpload?.reject(new Error("Recording was cancelled"));
  }, [cancelRecording]);

  React.useEffect(() => {
    const audioBlob = recorder.state.audio;
    const audioUploadUrl = audioUploadUrlRef.current;
    const pendingUpload = pendingUploadRef.current;

    if (!audioBlob || !audioUploadUrl || !pendingUpload) {
      return;
    }

    pendingUploadRef.current = null;
    setIsUploading(true);

    const uploadAudio = async () => {
      try {
        const response = await fetch(audioUploadUrl, {
          method: "PUT",
          body: audioBlob,
          headers: {
            "Content-Type": "audio/webm",
            "x-amz-server-side-encryption": "AES256",
          },
        });

        if (!response.ok) {
          throw new Error(`Audio upload failed with status ${response.status}`);
        }

        setIsUploading(false);
        pendingUpload.resolve({ audioBlob, audioDuration });
      } catch (error) {
        setIsUploading(false);
        pendingUpload.reject(
          error instanceof Error ? error : new Error(String(error))
        );
      }
    };

    uploadAudio();
  }, [recorder.state.audio, audioDuration]);

  return {
    isRecording: recorder.state.initRecording,
    isUploading,
    startListening,
    stopListening,
    cancelListening,
    audioBlob: recorder.state.audio || null,
    audioDuration,
    conversationLogId,
  };
}

export { useConversationRecording };
export type { UseConversationRecordingProps, UseConversationRecordingResult };
